import React from 'react';
import { useHistory } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import makeStyles from '@mui/styles/makeStyles';
import { useDataSyncContext } from '../../api/dataSync/context';
import { useDbManagerContext } from '../../api/dbManager/context';

const useStyles = makeStyles(() => ({
    root: {
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 360,
        backgroundColor: '#1e1e1e',
        color: '#d4d4d4',
        borderRadius: 10,
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        zIndex: 9999,
        overflow: 'hidden',
        fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
        fontSize: 12,
        border: '1px solid #444'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 12px',
        backgroundColor: '#333',
        borderBottom: '1px solid #444'
    },
    dots: {
        display: 'flex',
        gap: 5,
        '& span': {
            width: 8,
            height: 8,
            borderRadius: '50%',
            display: 'inline-block'
        }
    },
    actions: {
        display: 'flex',
        gap: 2
    },
    body: {
        padding: '8px 12px'
    },
    statusRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 6
    },
    progressBar: {
        borderRadius: 4,
        height: 6,
        backgroundColor: '#333',
        '& .MuiLinearProgress-bar': {
            borderRadius: 4
        }
    },
    currentTable: {
        color: '#569cd6',
        marginTop: 4,
        fontSize: 11
    },
    doneBar: {
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#6a9955'
        }
    },
    failedBar: {
        '& .MuiLinearProgress-bar': {
            backgroundColor: '#f44747'
        }
    }
}));

const STATUS_LABELS: Record<string, string> = {
    running: 'Running...',
    done: 'Completed',
    failed: 'Failed'
};

const STATUS_COLORS: Record<string, string> = {
    running: '#dcdcaa',
    done: '#6a9955',
    failed: '#f44747'
};

const DataSyncStatusBar = () => {
    const classes = useStyles();
    const history = useHistory();
    const { jobStatus: syncStatus, jobProgress, stopSync } = useDataSyncContext();
    const { jobStatus: dbmStatus, stopAction: stopDbm } = useDbManagerContext();

    // Don't show if we're already on the Database page
    if (window.location.pathname.includes('/database')) return null;

    // Determine which job to show (data-sync takes priority if both active)
    const showSync = syncStatus !== 'idle';
    const showDbm = dbmStatus !== 'idle';

    if (!showSync && !showDbm) return null;

    // Active job info
    const isDataSync = showSync;
    const status = isDataSync ? syncStatus : dbmStatus;
    const label = isDataSync ? 'data-sync' : 'db-manager';
    // Both job types now live on the one Database page, so the link no longer has to pick a route.
    const navPath = '/administration/setup/database/console';
    const dismiss = isDataSync ? stopSync : stopDbm;

    const statusLabel = STATUS_LABELS[status] || '';
    const statusColor = STATUS_COLORS[status] || '#808080';
    const percent = isDataSync ? (jobProgress?.percent ?? 0) : 0;

    const progressBarClass = `${classes.progressBar} ${
        status === 'done' ? classes.doneBar
        : status === 'failed' ? classes.failedBar
        : ''
    }`;

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.dots}>
                    <span style={{ background: status === 'running' ? '#27c93f' : '#ffbd2e' }} />
                    <span>{label}</span>
                </div>
                <div className={classes.actions}>
                    <IconButton
                        size="small"
                        onClick={() => history.push(navPath)}
                        title={`Open ${label}`}
                        style={{ color: '#ccc', padding: 2 }}
                    >
                        <OpenInNewIcon style={{ fontSize: 14 }} />
                    </IconButton>
                    {status !== 'running' && (
                        <IconButton
                            size="small"
                            onClick={dismiss}
                            title="Dismiss"
                            style={{ color: '#ccc', padding: 2 }}
                        >
                            <CloseIcon style={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                </div>
            </div>
            <div className={classes.body}>
                <div className={classes.statusRow}>
                    <span style={{ color: statusColor }}>{statusLabel}</span>
                    {isDataSync && jobProgress && (
                        <span>{jobProgress.completed}/{jobProgress.total} ({percent}%)</span>
                    )}
                    {!isDataSync && status === 'running' && (
                        <span style={{ color: '#569cd6' }}>in progress</span>
                    )}
                </div>
                <LinearProgress
                    variant={isDataSync ? 'determinate' : 'indeterminate'}
                    value={isDataSync ? percent : undefined}
                    className={progressBarClass}
                />
                {isDataSync && jobProgress?.currentTable && status === 'running' && (
                    <div className={classes.currentTable}>
                        {jobProgress.currentTable}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataSyncStatusBar;
